import * as React from "react";
import {useLayoutEffect} from "react";

import {useSelector} from "react-redux";
import styled from "styled-components";

import {Advice, StyledAdvice, truncateText} from "./Advice";
import {AdviceCard, StyledBaseAdvice} from "./AdviceCard";
import {AdviceCardTitle} from "./AdviceCardTitle";
import {AdviceHeader, StyledAdviceHeader} from "./AdviceHeader";
import {NoAdvice} from "./NoAdvice";
import {useAdviceLists} from "./useAdviceLists";
import {selectCheckStateFromMainState, selectSynonymList, selectTextLength} from "../../Store/AdviceState";
import { selectUserFeatures } from "../../Store/UserState";
import {Colors, Fonts, MediaBreakpoints} from "../../Styles";
import { GTMDataLayerPush } from "../../Util/GoogleTagManager";
import { hasAnyFeature } from "../../Util/UserUtils";
import { PremiumTeaser } from "../Advices/Util/PremiumTeaser";
import {LoadingDotsIcon} from "../Icons";
import { SynonymHint } from "../SynonymHint/SynonymHint";

import '../../Styles/component/authoring/advice/advice.sass';
import '../../Styles/component/authoring/pad/pad.sass';
import '../../Styles/component/authoring/tip/tip.sass';

const StyledLoadingIcon = styled.div`
    position: absolute;
    top: 110px;
    left: 50%;
    transform: translateX(-50%);
    height: 15px;

    @media ${MediaBreakpoints.medium} {
        top: 70px;
    }    
    
    @media ${MediaBreakpoints.wide} {
        top: 150px;
    }
    
    @media (min-width: 1310px) {
        top: 90px;
    }
`;

const StyledSynonymDetails = styled.details`
    border-top: 1px solid ${Colors.iron};

    &[open] summary {
        font-weight: bold;
        &:after {
            content: "-";
            font-weight: 400;
        }
    }

    div {
        margin-left: 4px;
        font-size: 15px;
        margin-bottom: 15px;
    }

    summary {
        list-style: none;
        position: relative;
        padding: 10px 1.3em;
        font-family: ${Fonts.publico};
        font-size: 23px;
        transition: background-color 0.2s ease-in;
        
        span {
            display: block;
            max-width: 100%;
            white-space: nowrap;
            word-wrap: break-word;
            -webkit-hyphens: auto;
            -ms-hyphens: auto;
            -moz-hyphens: auto;
            hyphens: auto;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &:before {
            content: "≈";
            position: absolute;
            left: 4px;
        }

        &::marker,
        &::-webkit-details-marker {
            display: none;
        }

        &:hover {
            cursor: pointer;
            background-color: ${Colors.mystic};

        }
        &:focus {
            outline: none;
        }

        &::after {
            content: "+";
            color: ${Colors.osloGray};
            position: absolute;
            font-size: 20px;
            font-weight: 400;
            font-family: ${Fonts.theinhardt};
            line-height: 1;
            right: 4px;
            top: 50%;
            transform: translateY(-50%);

        }
    }
`;

const StyledAdditionalCards = styled.div`
    ${StyledBaseAdvice};
    border-radius: 10px;
    cursor: pointer;
    margin: 0 0 15px;
    padding: 15px 20px;
    transition: box-shadow 0.3s ease-in-out;

    &:hover {
        box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.3)
    }

    h2 {
        justify-content: space-between;
        margin-bottom: 0;
    }
`;

export const AdvicePanel = () => {
    const localStorage: Storage = window.localStorage;
    const showSynonymHintInitialValue = typeof localStorage !== 'undefined' && localStorage.getItem('hideSynonymHint') !== 'true';
    const [showSynonymHint, setShowSynonymHint] = React.useState(showSynonymHintInitialValue);
    const [adviceCardType, setAdviceCardType] = React.useState<"SPELL-ERROR" | "STYLE-ERROR" | "SYNONYMS" | undefined>();
    const synonymList = useSelector(selectSynonymList);
    const isLoading = useSelector(selectCheckStateFromMainState).isLoading;
    const textLength = useSelector(selectTextLength);
    const userFeatures = useSelector(selectUserFeatures)
    const {
        currentAdvice,
        spellAdvices,
        styleAdvices,
        nextAdvice,
        previousAdvice,
        setCurrentList,
    } = useAdviceLists();
    const currentAdviceType = currentAdvice?.type;

    const closeSynonymHint = () => {
        setShowSynonymHint(false);
        localStorage.setItem('hideSynonymHint', 'true');
    }

    /**
     * show synonym card, when synonym list changed
     */
    useLayoutEffect(() => {
        setAdviceCardType(synonymList.length > 0 ? "SYNONYMS" : currentAdvice?.type);
    }, [synonymList]);

    /**
     * hide synonyms when current advice is changed (i.e. mouse click on advice in text)
     */
    useLayoutEffect(() => {
        setAdviceCardType(currentAdvice?.type);
    }, [currentAdviceType]);

    const onClickSpellErrorCard = () => {
        if (currentAdvice?.type !== "SPELL-ERROR") {
            setCurrentList("SPELL-ERROR");
        }
        setAdviceCardType("SPELL-ERROR");
    }
    const onClickStyleErrorCard = () => {
        if (currentAdvice?.type !== "STYLE-ERROR") {
            setCurrentList("STYLE-ERROR");
        }
        setAdviceCardType("STYLE-ERROR");
    }
    const onClickSynonymCard = () => {
        GTMDataLayerPush({event: "advice-type-selection", "advice-type": "synonyms", location: "advice-panel"})
        setAdviceCardType("SYNONYMS");
    }

    const Loader = isLoading ? (
        <StyledLoadingIcon>
            <LoadingDotsIcon fill={Colors.mineShaft} />
        </StyledLoadingIcon>
    ) : null

    const SynonymCard = (adviceCardType === "SYNONYMS" && hasAnyFeature(userFeatures, "synonyms")) ? (
        <StyledAdvice>
            <AdviceCard alwaysOpen={true}>
                <AdviceCardTitle onClick={e => e.preventDefault()}>
                    <StyledAdviceHeader counterColor={Colors.pastelGreen}>
                        Synonyme
                    </StyledAdviceHeader>
                </AdviceCardTitle>
                {synonymList.map((synonymGroup, index) => (
                    <StyledSynonymDetails key={index}>
                        <summary><span>{truncateText(synonymGroup.description)}</span></summary>
                        <div>{synonymGroup.synonyms.join(", ")}</div>
                    </StyledSynonymDetails>
                ))}
            </AdviceCard>
        </StyledAdvice>
    ) : null;

    const CurrentCard = isLoading ? null : !currentAdvice ? (<NoAdvice />) : adviceCardType !== "SYNONYMS" && (
        <Advice
            data={currentAdvice.advice}
            adviceType={currentAdvice.type}
            adviceHeader={
                <AdviceHeader
                    adviceType={currentAdvice.type}
                    index={currentAdvice.index}
                    length={currentAdvice.list.length}
                    onClickNext={nextAdvice}
                    onClickPrevious={previousAdvice}
                />
            }
        />
    );

    const AdditionalCards: JSX.Element[] = [];
    if (adviceCardType !== "SPELL-ERROR" && spellAdvices.length > 0) {
        AdditionalCards.push((
            <StyledAdditionalCards key={1}>
                <StyledAdviceHeader counterColor={Colors.supernova} onClick={onClickSpellErrorCard}>
                    Rechtschreibung und Grammatik
                    <span>{spellAdvices.length}</span>
                </StyledAdviceHeader>
            </StyledAdditionalCards>
        ));
    }
    if (adviceCardType !== "STYLE-ERROR" && styleAdvices.length > 0) {
        AdditionalCards.push((
            <StyledAdditionalCards key={2}>
                <StyledAdviceHeader counterColor={Colors.periwinkle} onClick={onClickStyleErrorCard}>
                    Stil und Vokabular
                    <span>{styleAdvices.length}</span>
                </StyledAdviceHeader>
            </StyledAdditionalCards>
        ));
    }
    if (adviceCardType !== "SYNONYMS" && synonymList.length > 0) {
        AdditionalCards.push((
            <StyledAdditionalCards key={3}>
                <StyledAdviceHeader counterColor={Colors.pastelGreen} onClick={onClickSynonymCard}>
                    Synonyme
                    <span>{synonymList.length}</span>
                </StyledAdviceHeader>
            </StyledAdditionalCards>
        ));
    }

    return (
        <div id="advice-panel" style={{marginBottom: 20}}>
            {Loader}
            {CurrentCard}
            {SynonymCard}
            {AdditionalCards}
            {textLength > 0 && <PremiumTeaser />}
            {(hasAnyFeature(userFeatures, "synonyms") && showSynonymHint && textLength > 0) && <SynonymHint onClick={closeSynonymHint} /> }
        </div>
    );
}
